import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TriangleAlert,
} from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import * as React from 'react';
import { useNimbusPortalBridgeAttributes } from '../../Nimbus3Scope';
import { cn } from '../../lib/utils';

/**
 * Select — Generated from Figma Nimbus v3 Design System
 * @figma-node 345:11530
 * @figma-url https://www.figma.com/design/euN3FdxtNlCC8GiKC66YmG/Nimbus-v3--TBD-?node-id=345:11530
 *
 * Variants: Default, Focus, Filled, Filled(Focus), Disabled, Error, Error(focus), Success, Success(focus)
 *
 * All colors extracted from Figma API — not guessed.
 */

function Select({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  children,
  error = false,
  success = false,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  error?: boolean;
  success?: boolean;
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-error={error || undefined}
      data-success={success || undefined}
      className={cn(
        /*
         * Base — styled like Input atom
         * Figma: h=36, border=#cecece, radius=8, padding=12/4,
         * shadow 0 1 2 0 #202020/5%
         */
        'flex h-9 w-full items-center gap-2 rounded-lg border bg-white px-3 py-1 text-sm',
        'text-nimbus-foreground border-nimbus-input shadow-nimbus-xs',
        'transition-all whitespace-nowrap outline-none',

        /* Placeholder text */
        'data-[placeholder]:text-nimbus-muted-foreground',

        /* Hover — Figma: stroke=#727272 */
        'hover:border-nimbus-muted-foreground',

        /* Focus — Figma: stroke=#1e5eed, ring 3px spread #a3a3a3/50 */
        'focus:border-nimbus-focus focus:shadow-nimbus-focus-outline',

        /* Disabled — Figma: opacity=0.5 */
        'disabled:cursor-not-allowed disabled:opacity-50',

        /* Error state — Figma: stroke=#db2929 */
        error && 'border-nimbus-destructive hover:border-nimbus-destructive',
        error &&
          'focus:border-nimbus-destructive focus:shadow-nimbus-focus-destructive',

        /* Success state — Figma: stroke=#63bc54 */
        success &&
          'border-nimbus-positive-base hover:border-nimbus-positive-base',
        success &&
          'focus:border-nimbus-positive-base focus:shadow-nimbus-focus-positive',

        /* Dark mode */
        'dark:bg-white/[0.045] dark:border-white/[0.15]',

        /* Icon styling */
        "[&_svg:not([class*='text-'])]:text-nimbus-muted-foreground",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",

        /* Value — flex-1 so it fills space, pushing chevron to far right */
        '*:data-[slot=select-value]:flex-1 *:data-[slot=select-value]:min-w-0',
        '*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2',

        className
      )}
      {...props}
    >
      {error && (
        <TriangleAlert className="size-4 shrink-0 text-nimbus-destructive" />
      )}
      {success && (
        <CheckIcon className="size-4 shrink-0 text-nimbus-positive-base" />
      )}
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon
          className={cn(
            'size-4',
            error
              ? 'text-nimbus-destructive'
              : success
                ? 'text-nimbus-positive-base'
                : 'text-nimbus-muted-foreground'
          )}
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  const portalBridgeAttributes = useNimbusPortalBridgeAttributes();

  return (
    <SelectPrimitive.Portal>
      <div
        data-radix-portal=""
        data-slot="select-portal"
        {...portalBridgeAttributes}
      >
        <SelectPrimitive.Content
          data-slot="select-content"
          position={position}
          className={cn(
            /* Base */
            'relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] overflow-x-hidden overflow-y-auto',
            'bg-nimbus-background text-nimbus-foreground border border-nimbus-border rounded-lg shadow-nimbus-md',

            /* Animation */
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-2',
            'data-[side=left]:slide-in-from-right-2',
            'data-[side=right]:slide-in-from-left-2',
            'data-[side=top]:slide-in-from-bottom-2',

            /* Popper positioning */
            position === 'popper' &&
              'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',

            className
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              'p-1',
              position === 'popper' &&
                'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1'
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </div>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn(
        'text-nimbus-muted-foreground px-2 py-1.5 text-xs font-medium',
        className
      )}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        /* Base */
        'relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none',

        /* Hover/Focus — Figma: bg=nimbus-muted */
        'focus:bg-nimbus-muted',

        /* Disabled */
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',

        /* Icon styling */
        "[&_svg:not([class*='text-'])]:text-nimbus-muted-foreground",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",

        className
      )}
      {...props}
    >
      <span
        data-slot="select-item-indicator"
        className="absolute right-2 flex size-3.5 items-center justify-center"
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn(
        'pointer-events-none -mx-1 my-1 h-px bg-nimbus-border',
        className
      )}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
